import React from 'react';
import {
  LayoutGrid,
  Users,
  ShoppingCart,
  Clock,
  Shield,
  BookOpen,
  Calculator,
  Home,
  BarChart3,
  FolderOpen,
  Layers,
  Target,
  Zap,
  FileCheck,
  TrendingUp,
} from 'lucide-react';
import { deckImage } from '../components/deck/deckAssets';

/** Delta Constructors stack — category and label for display */
const STACK_ITEMS = [
  { icon: LayoutGrid, label: 'ERP', value: 'Sage Intacct', color: 'bg-blue-500' },
  { icon: Users, label: 'HRMS', value: 'Sage HRMS (Criterion HMS)', color: 'bg-indigo-500' },
  { icon: ShoppingCart, label: 'Procurement', value: 'Coupa', color: 'bg-emerald-500' },
  { icon: Clock, label: 'Timekeeping', value: 'RiskCast', color: 'bg-amber-500' },
  { icon: Shield, label: 'Safety', value: 'AIC One Global → in-house', color: 'bg-red-500' },
  { icon: BookOpen, label: 'LMS', value: 'Litmos', color: 'bg-violet-500' },
  { icon: Calculator, label: 'Estimating', value: 'WinEst', color: 'bg-cyan-500' },
  { icon: Home, label: 'Billeting/Housing', value: 'Nomadis', color: 'bg-teal-500' },
  { icon: BarChart3, label: 'Project Controls', value: 'PCDB (in-house)', color: 'bg-primary-600', highlight: true },
  { icon: FolderOpen, label: 'File Storage', value: 'Egnyte', color: 'bg-slate-500' },
];

/**
 * Client Discovery Session page tailored for Delta Constructors.
 * Visual layout with stack overview and Jobotics integration narrative.
 * Access at /discover-delta (e.g. https://demo.jobotics.ai/discover-delta).
 */
export default function DiscoverDelta() {
  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      {/* Hero — light background so logo is visible */}
      <header className="relative bg-gradient-to-br from-white via-slate-50 to-primary-50 text-gray-900 overflow-hidden border-b border-slate-200">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%231e293b\' fill-opacity=\'0.04\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-80" aria-hidden="true" />
        <div className="relative mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-16">
          <img src={deckImage('/jobotics-logo.png')} alt="Jobotics" className="h-10 w-auto mb-8" />
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl lg:text-5xl">
            Jobotics AI — Client Discovery Session
          </h1>
          <p className="mt-4 text-xl text-gray-700 max-w-2xl">
            Tailored for{' '}
            <a
              href="https://www.deltaconstructors.net/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 font-semibold underline underline-offset-4 hover:text-primary-700"
            >
              Delta Constructors
            </a>
            — full-service construction and fabrication for industrial, petroleum, gas processing, and energy projects.
          </p>
          <p className="mt-3 text-gray-600 max-w-2xl">
            Explore how Jobotics can sit on top of your PCDB and connected systems to unlock real-time intelligence, automate reporting, and accelerate decisions.
          </p>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-16 space-y-20">
        {/* Your technology stack — visual grid */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
            <LayoutGrid className="h-7 w-7 text-primary-600" />
            Your technology stack
          </h2>
          <p className="text-gray-600 mb-8 max-w-2xl">
            A snapshot of the systems that power Delta today. PCDB is your central project controls and reporting engine — the natural place for Jobotics to integrate.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {STACK_ITEMS.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.label}
                  className={`rounded-xl border-2 bg-white p-4 shadow-sm transition hover:shadow-md ${
                    item.highlight ? 'border-primary-500 ring-2 ring-primary-200' : 'border-gray-200'
                  }`}
                >
                  <div className={`inline-flex rounded-lg p-2 ${item.color} text-white`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="mt-3">
                    <p className="text-xs font-medium uppercase tracking-wider text-gray-500">{item.label}</p>
                    <p className="text-sm font-semibold text-gray-900 mt-0.5">{item.value}</p>
                    {item.highlight && (
                      <p className="text-xs text-primary-600 font-medium mt-1">Core PM / reporting</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="mt-4 text-sm text-gray-500">
            CRM: n/a · Safety currently migrating from AIC One Global to in-house database/application.
          </p>
        </section>

        {/* Where Jobotics fits — visual */}
        <section className="rounded-2xl border-2 border-primary-200 bg-white p-6 sm:p-8 shadow-lg">
          <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
            <Layers className="h-7 w-7 text-primary-600" />
            Where Jobotics fits
          </h2>
          <p className="text-gray-700 mb-8 max-w-3xl">
            <strong>PCDB</strong> is your main project management tool — an in-house application that pulls data from Intacct, Coupa commitments, WinEst estimates, and field progress sheets to generate internal and external cost reports. Jobotics can connect to <strong>PCDB and what it already uses</strong>. The rest of your stack — systems that are <strong>not</strong> connected by PCDB, such as HRMS, RiskCast, Litmos, Nomadis, Egnyte, safety, and more — can also be supported by Jobotics, so you get one AI layer across all the systems that matter. Unlock natural-language answers, automated reporting, and AI-driven insights without replacing what already works.
          </p>
          <div className="flex flex-col items-center gap-4 py-6">
            {/* Row 1: Data sources that feed PCDB */}
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Data sources</p>
            <div className="flex flex-wrap justify-center gap-3">
              <div className="rounded-xl bg-slate-100 px-4 py-3 text-center min-w-[7rem]">
                <p className="text-xs font-medium text-slate-500">ERP</p>
                <p className="text-sm font-semibold text-slate-800">Sage Intacct</p>
              </div>
              <div className="rounded-xl bg-slate-100 px-4 py-3 text-center min-w-[7rem]">
                <p className="text-xs font-medium text-slate-500">Procurement</p>
                <p className="text-sm font-semibold text-slate-800">Coupa</p>
              </div>
              <div className="rounded-xl bg-slate-100 px-4 py-3 text-center min-w-[7rem]">
                <p className="text-xs font-medium text-slate-500">Estimating</p>
                <p className="text-sm font-semibold text-slate-800">WinEst</p>
              </div>
              <div className="rounded-xl bg-slate-100 px-4 py-3 text-center min-w-[7rem]">
                <p className="text-xs font-medium text-slate-500">Field</p>
                <p className="text-sm font-semibold text-slate-800">Progress sheets</p>
              </div>
            </div>
            <p className="text-slate-400 text-sm">feed into</p>
            {/* Row 2: PCDB — your hub */}
            <div className="rounded-xl bg-primary-600 text-white px-8 py-5 text-center ring-2 ring-primary-300 shadow-md w-full max-w-md">
              <p className="text-xs font-medium text-primary-100">Project controls</p>
              <p className="text-lg font-bold mt-1">PCDB</p>
              <p className="text-xs text-primary-200 mt-1">Cost reports · tracking · in-house</p>
            </div>
            <p className="text-slate-400 text-sm">Jobotics connects to PCDB; also supports the rest of your stack</p>
            {/* Row 3: Jobotics — AI on top */}
            <div className="rounded-xl bg-primary-500 text-white px-8 py-5 text-center shadow-md w-full max-w-md">
              <p className="text-xs font-medium text-primary-100">AI layer</p>
              <p className="text-lg font-bold mt-1">Jobotics</p>
              <p className="text-xs text-primary-200 mt-1">Answers · automation · reporting</p>
              <p className="text-xs text-primary-200/90 mt-2 font-medium">PCDB + HRMS, RiskCast, Litmos, Nomadis, Egnyte, safety, etc.</p>
            </div>
          </div>
        </section>

        {/* Welcome */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Welcome to Jobotics</h2>
          <p className="text-lg leading-relaxed text-gray-700 mb-4">
            This session is the starting point for a smarter way to run your construction and fabrication operations. Jobotics AI is purpose-built for industrial contractors like Delta Constructors — connecting your systems (including PCDB), automating your workflows, and delivering the real-time intelligence your teams need to deliver safe, high-quality projects on time and on budget.
          </p>
          <p className="text-lg leading-relaxed text-gray-700">
            Today&apos;s conversation is about understanding your world before prescribing a solution.
          </p>
        </section>

        {/* Session objective — visual cards */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
            <Target className="h-7 w-7 text-primary-600" />
            Session objective
          </h2>
          <p className="text-gray-600 mb-8">
            Our goal is to leave this session with a shared picture of your systems, pain points, and a concrete path forward.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { n: '1', title: 'Systems & workflows', desc: 'Map ERP, PCDB, Coupa, WinEst, and document storage to find integration points.' },
              { n: '2', title: 'Fragmentation & bottlenecks', desc: 'Surface where data is siloed and where reporting consumes time.' },
              { n: '3', title: 'High-ROI AI opportunities', desc: 'Pinpoint workflows where AI automation delivers the fastest return.' },
              { n: '4', title: 'Pilot scope', desc: 'Define a focused, low-risk starting point that builds confidence for rollout.' },
            ].map((item) => (
              <div key={item.n} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 text-primary-700 font-bold text-sm">
                  {item.n}
                </span>
                <h3 className="mt-3 font-semibold text-gray-900">{item.title}</h3>
                <p className="mt-1 text-sm text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Core capabilities — visual */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Zap className="h-7 w-7 text-primary-600" />
            Core capabilities
          </h2>
          <p className="text-gray-700 mb-8 max-w-2xl">
            Jobotics delivers four integrated capability areas that sit on top of your existing stack.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { title: 'AI Answer Engine', desc: 'Natural language queries across PCDB and connected systems — accurate answers in seconds.', icon: '💬' },
              { title: 'AI Analyst', desc: 'Automated financial and operational insights: CPI, SPI, budget variance, cash flow — in real time.', icon: '📊' },
              { title: 'Workflow automation', desc: 'Automated reporting pipelines, alerts, and triggered actions that cut repetitive manual work.', icon: '⚡' },
              { title: 'AI agents', desc: 'Specialized agents for PM, estimating, and contract review that take action and flag risks.', icon: '🤖' },
            ].map((cap) => (
              <div key={cap.title} className="flex gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                <span className="text-2xl">{cap.icon}</span>
                <div>
                  <h3 className="font-semibold text-gray-900">{cap.title}</h3>
                  <p className="text-sm text-gray-600 mt-1">{cap.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Example use cases — visual cards */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <FileCheck className="h-7 w-7 text-primary-600" />
            Example use cases
          </h2>
          <div className="space-y-8">
            <div className="rounded-2xl border-2 border-gray-200 bg-white overflow-hidden shadow-sm">
              <div className="bg-primary-50 px-6 py-3 border-b border-primary-100">
                <h3 className="font-bold text-gray-900">Project intelligence & reporting</h3>
              </div>
              <div className="grid sm:grid-cols-2 gap-6 p-6">
                <div>
                  <h4 className="text-sm font-semibold text-red-700 mb-2">Before</h4>
                  <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                    <li>Manual aggregation across PCDB, Intacct, Coupa</li>
                    <li>5–8 hrs/week per PM on reporting</li>
                    <li>Stale data by the time reports reach leadership</li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-green-700 mb-2">After Jobotics</h4>
                  <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                    <li>Automated reporting from connected systems</li>
                    <li>Real-time CPI/SPI and cost visibility</li>
                    <li>Live project health for executives</li>
                  </ul>
                </div>
              </div>
              <div className="px-6 pb-6 flex flex-wrap gap-2">
                <span className="rounded-lg bg-primary-100 px-3 py-1 text-sm font-medium text-primary-800">90% faster reports</span>
                <span className="rounded-lg bg-primary-100 px-3 py-1 text-sm font-medium text-primary-800">6–8 hrs saved/PM/week</span>
              </div>
            </div>

            <div className="rounded-2xl border-2 border-gray-200 bg-white overflow-hidden shadow-sm">
              <div className="bg-primary-50 px-6 py-3 border-b border-primary-100">
                <h3 className="font-bold text-gray-900">Estimation automation</h3>
              </div>
              <div className="grid sm:grid-cols-2 gap-6 p-6">
                <div>
                  <h4 className="text-sm font-semibold text-red-700 mb-2">Before</h4>
                  <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                    <li>Estimates from WinEst + manual handoffs to PCDB</li>
                    <li>Re-keying and version drift</li>
                    <li>Bid prep and takeoffs consume days</li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-green-700 mb-2">After Jobotics</h4>
                  <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                    <li>AI-powered labor/cost estimation from connected data</li>
                    <li>Consistent, auditable estimates; fewer errors</li>
                    <li>Faster bid cycles and reuse of historical patterns</li>
                  </ul>
                </div>
              </div>
              <div className="px-6 pb-6 flex flex-wrap gap-2">
                <span className="rounded-lg bg-cyan-100 px-3 py-1 text-sm font-medium text-cyan-800">Faster bid cycles</span>
                <span className="rounded-lg bg-cyan-100 px-3 py-1 text-sm font-medium text-cyan-800">WinEst + PCDB aligned</span>
              </div>
            </div>

            <div className="rounded-2xl border-2 border-gray-200 bg-white overflow-hidden shadow-sm">
              <div className="bg-primary-50 px-6 py-3 border-b border-primary-100">
                <h3 className="font-bold text-gray-900">Contract review</h3>
              </div>
              <p className="p-6 text-gray-700 text-sm">
                AI automatically reviews incoming contracts, flagging unfavorable payment terms, liability, and insurance gaps against your standards — delivering a prioritized risk summary in minutes instead of days.
              </p>
            </div>
          </div>
        </section>

        {/* Pilot — visual */}
        <section className="rounded-2xl border-2 border-primary-200 bg-white p-6 sm:p-8 shadow-lg">
          <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
            <TrendingUp className="h-7 w-7 text-primary-600" />
            Pilot approach — value in 2 weeks
          </h2>
          <p className="text-gray-700 mb-8">
            Structured to deliver tangible results in 2 weeks: 3 days consulting and prototype, 1 week production and iteration.
          </p>
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="rounded-xl border-2 border-amber-200 bg-amber-50 p-6">
              <p className="text-sm font-bold text-amber-800">Days 1–3</p>
              <p className="text-lg font-semibold text-gray-900 mt-1">Consulting & prototype</p>
              <p className="text-gray-700 mt-2 text-sm">
                Discovery alignment, connect priority systems (e.g. PCDB, Intacct), validate data, and build a working prototype of your first use case.
              </p>
            </div>
            <div className="rounded-xl border-2 border-green-200 bg-green-50 p-6">
              <p className="text-sm font-bold text-green-800">Week 2</p>
              <p className="text-lg font-semibold text-gray-900 mt-1">Production & iteration</p>
              <p className="text-gray-700 mt-2 text-sm">
                Deploy to production, onboard core users, run live against real project data, and iterate daily based on feedback.
              </p>
            </div>
          </div>
          <p className="text-gray-600 mt-6 text-sm">
            Success metrics: <strong>Time saved</strong>, <strong>Adoption rate</strong>, <strong>Decision speed</strong>.
          </p>
        </section>

        {/* Next steps */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Next steps</h2>
          <ol className="space-y-3 text-gray-700">
            <li><strong>1.</strong> Identify initial use case — e.g. PCDB reporting automation or WinEst–PCDB estimation flow.</li>
            <li><strong>2.</strong> Confirm integration systems — PCDB first; then Intacct, Coupa, Egnyte as needed.</li>
            <li><strong>3.</strong> Align on pilot scope & timeline — 2 weeks (3 days consulting & prototype, 1 week production & iteration).</li>
            <li><strong>4.</strong> Kick off implementation — Sign pilot agreement, schedule kickoff, introduce your dedicated Jobotics team.</li>
          </ol>
        </section>

        {/* Thank You / CTA */}
        <section className="rounded-2xl bg-gradient-to-br from-slate-800 to-primary-800 text-white p-8 sm:p-10 text-center">
          <h2 className="text-2xl font-bold mb-4">Thank you</h2>
          <p className="text-slate-200 text-lg mb-6 max-w-2xl mx-auto">
            We appreciate the time and candor you brought to today&apos;s session. We look forward to building something exceptional with Delta Constructors — and to laying Jobotics on top of the systems that already power your success.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="https://www.deltaconstructors.net/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center rounded-lg border-2 border-white px-6 py-3 text-base font-semibold text-white hover:bg-white/10"
            >
              Delta Constructors
            </a>
          </div>
        </section>
      </main>

      <footer className="border-t border-gray-200 bg-white py-6">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 text-center text-sm text-gray-500">
          Jobotics AI — AI-Native Operating System for Contractors. Connecting your data. Automating your work. Accelerating your decisions.
        </div>
      </footer>
    </div>
  );
}
